import { useEffect, useState } from "react";
import { getData } from "../api";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import theme from "../styles/theme";
import CategoryRanking from "../components/CategoryRanking";
import ExerciseRanking from "../components/ExerciseRanking";
import PlayerRadarComparison from "../components/PlayerRadarComparison";
import {
  DEFAULT_SPECIAL_ASSIGNMENTS,
  SPECIAL_CATEGORIES,
  countExercisesForSpecialCategory,
  filterOutSpecialCategories,
  loadSpecialAssignments,
  saveSpecialAssignments,
} from "../utils/specialCategories";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';


interface User {
  id: number;
  username: string;
  nom_complet?: string;
  equipe?: string;
  image_url?: string;
}

interface Evaluation {
  id: number;
  player_id: number;
  exercise_id: number;
  details: string;
  session_id?: number; // ajouté pour corriger l'erreur TS
}

interface Exercise {
  id: number;
  max_points: number;
  repetitions: number;
  category_id: number;
  title: string;
  description: string;
}

interface Category {
  id: number;
  name: string;
}

function Dashboard() {
  const [players, setPlayers] = useState<User[]>([]);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<number | null>(null);
  const [teams, setTeams] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [useCoef, setUseCoef] = useState(true);
  const [specialAssignments, setSpecialAssignments] = useState<{
    [exerciseId: number]: {
      mobilite: boolean;
      puissance: boolean;
      construction: boolean;
      agressivite: boolean;
    };
  }>({});
  const [isExporting, setIsExporting] = useState(false);
  const [openPlanModal, setOpenPlanModal] = useState(false);
  const [planMarkdown, setPlanMarkdown] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setSpecialAssignments(loadSpecialAssignments() as any);
  }, []);

  useEffect(() => {
    if (Object.keys(specialAssignments).length > 0) {
      saveSpecialAssignments(specialAssignments);
    }
  }, [specialAssignments]);

  const toggleSpecialCategory = (
    exerciseId: number,
    category: "mobilite" | "puissance" | "construction" | "agressivite"
  ) => {
    setSpecialAssignments((prev) => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        [category]: !prev[exerciseId]?.[category],
      },
    }));
  };

  useEffect(() => {
    const storedTeam = localStorage.getItem("selectedTeam");
    if (storedTeam) {
      setSelectedTeam(Number(storedTeam));
    }
  }, []);

  useEffect(() => {
    getData("teams").then((res: any) => setTeams(res.data || []));
  }, []);

  useEffect(() => {
    let teamId = selectedTeam;
    if (!teamId) {
      const storedTeam = localStorage.getItem("selectedTeam");
      if (storedTeam) {
        teamId = Number(storedTeam);
        setSelectedTeam(teamId);
      }
    }
    if (teamId) {
      getData("sessions", {
        where: `team = ${teamId}`,
        order_by: "start_date DESC",
      }).then((res: any) => {
        setSessions(res.data || []);
        if (res.data && res.data.length > 0) {
          const sorted = [...res.data].sort(
            (a, b) =>
              new Date(b.start_date).getTime() -
              new Date(a.start_date).getTime()
          );
          setSelectedSession(sorted[0].id);
        } else {
          setSelectedSession(null);
        }
      });
    } else {
      setSessions([]);
      setSelectedSession(null);
    }
  }, [selectedTeam]);

  useEffect(() => {
    getData("users", { where: "role = 'player'" }).then(async (res: any) => {
      const users = res.data || [];
      const playersWithProfiles = await Promise.all(
        users.map(async (user: any) => {
          const profileRes = await getData("profiles", {
            where: `user_id = '${user.id}'`,
          });
          const profile = profileRes.data?.[0] || {};
          return {
            ...user,
            nom_complet: profile.nom_complet,
            equipe: profile.equipe,
          };
        })
      );
      setPlayers(playersWithProfiles);
    });

    getData("exercises").then((res: any) => {
      setExercises(res.data || []);
    });

    getData("exercise_categories").then((res: any) => {
      setCategories(filterOutSpecialCategories(res.data || []));
    });

    if (selectedSession) {
      getData("evaluations", {
        where: `session_id = ${selectedSession}`,
      }).then((res: any) => {
        setEvaluations(res.data || []);
      });
    } else {
      setEvaluations([]);
    }
  }, [selectedSession]);

  const exerciseMap: { [id: number]: Exercise } = {};
  exercises.forEach((ex) => {
    exerciseMap[ex.id] = ex;
  });

  function getCoefFromTitle(title: string): number {
    if (title.includes("niv. 4")) return 2.5;
    if (title.includes("niv. 3")) return 2;
    if (title.includes("niv. 2")) return 1.5;
    if (title.includes("niv. 1")) return 1;
    return 1;
  }

  const computePlayerCategoryRates = (playerId: number) => {
    const agg: {
      [cid: number]: { weightedTotal: number; coefSum: number; count: number };
    } = {};
    const evalsOfPlayer = evaluations.filter(
      (ev) => ev.player_id === playerId
    );

    evalsOfPlayer.forEach((ev) => {
      const ex = exerciseMap[ev.exercise_id];
      if (!ex) return;
      const cid = ex.category_id;

      let successRate = 0;
      try {
        const scores: number[] = JSON.parse(ev.details || "[]");
        const totalScore = scores.reduce((a, b) => a + b, 0);
        const maxPoints = ex.max_points || 0;
        const repetitions = ex.repetitions || 0;
        const maxPossible = maxPoints * repetitions;
        if (maxPossible > 0) {
          successRate = parseFloat(
            ((totalScore / maxPossible) * 100).toFixed(2)
          );
        }
      } catch {
        successRate = 0;
      }

      const coef = useCoef ? getCoefFromTitle(ex.title) : 1;

      if (!agg[cid]) {
        agg[cid] = { weightedTotal: 0, coefSum: 0, count: 0 };
      }
      agg[cid].weightedTotal += successRate * coef;
      agg[cid].coefSum += coef;
      agg[cid].count += 1;
    });

    const result: { [cid: number]: { rate: number; count: number } } = {};
    Object.entries(agg).forEach(
      ([cidStr, { weightedTotal, coefSum, count }]) => {
        const cid = parseInt(cidStr, 10);
        result[cid] = {
          rate:
            coefSum > 0
              ? parseFloat((weightedTotal / coefSum).toFixed(2))
              : 0,
          count,
        };
      }
    );

    return result;
  };

  const computeSpecialRates = (
    playerId: number,
    category: "mobilite" | "puissance" | "construction" | "agressivite"
  ) => {
    const exerciseIds = Object.entries(specialAssignments)
      .filter(([_, assignments]) => assignments[category])
      .map(([id, _]) => Number(id));

    const evalsOfPlayer = evaluations.filter(
      (ev) => ev.player_id === playerId && exerciseIds.includes(ev.exercise_id)
    );

    let weightedTotal = 0;
    let coefSum = 0;
    let count = 0;

    evalsOfPlayer.forEach((ev) => {
      const ex = exerciseMap[ev.exercise_id];
      if (!ex) return;

      let successRate = 0;
      try {
        const scores: number[] = JSON.parse(ev.details || "[]");
        const totalScore = scores.reduce((a, b) => a + b, 0);
        const maxPoints = ex.max_points || 0;
        const repetitions = ex.repetitions || 0;
        const maxPossible = maxPoints * repetitions;
        if (maxPossible > 0) {
          successRate = parseFloat(
            ((totalScore / maxPossible) * 100).toFixed(2)
          );
        }
      } catch {
        successRate = 0;
      }

      const coef = useCoef ? getCoefFromTitle(ex.title) : 1;
      weightedTotal += successRate * coef;
      coefSum += coef;
      count += 1;
    });

    return {
      rate:
        coefSum > 0
          ? parseFloat((weightedTotal / coefSum).toFixed(2))
          : 0,
      count,
    };
  };

  const rankingByCategory: {
    [cid: number]: { player: User; rate: number; count: number }[];
  } = {};

  categories.forEach((cat) => {
    const arr: { player: User; rate: number; count: number }[] = players.map(
      (p) => {
        const rates = computePlayerCategoryRates(p.id);
        return {
          player: p,
          rate: rates[cat.id]?.rate ?? 0,
          count: rates[cat.id]?.count ?? 0,
        };
      }
    );

    arr.sort((a, b) => b.rate - a.rate);
    rankingByCategory[cat.id] = arr;
  });

  const specialCategories = [...SPECIAL_CATEGORIES].filter(
    (special) => countExercisesForSpecialCategory(specialAssignments as any, special.id) > 0
  );

  const rankingBySpecial: {
    [key: string]: { player: User; rate: number; count: number }[];
  } = {};

  specialCategories.forEach((special) => {
    const arr: { player: User; rate: number; count: number }[] = players.map(
      (p) => {
        const result = computeSpecialRates(
          p.id,
          special.id as "mobilite" | "puissance" | "construction" | "agressivite"
        );
        return {
          player: p,
          rate: result.rate,
          count: result.count,
        };
      }
    );

    arr.sort((a, b) => b.rate - a.rate);
    rankingBySpecial[special.id] = arr;
  });

  const rankingByExercise: {
    [eid: number]: { player: User; rate: number }[];
  } = {};

  exercises.forEach((ex) => {
    const arr: { player: User; rate: number }[] = players.map((p) => {
      const evals = evaluations.filter(
        (ev) => ev.player_id === p.id && ev.exercise_id === ex.id
      );
      let rate = 0;
      if (evals.length > 0) {
        try {
          const scores: number[] = JSON.parse(evals[0].details || "[]");
          const totalScore = scores.reduce((a, b) => a + b, 0);
          const maxPoints = ex.max_points || 0;
          const repetitions = ex.repetitions || 0;
          const maxPossible = maxPoints * repetitions;
          if (maxPossible > 0) {
            rate = parseFloat(
              ((totalScore / maxPossible) * 100).toFixed(2)
            );
          }
        } catch {
          rate = 0;
        }
      }
      return { player: p, rate };
    });
    arr.sort((a, b) => b.rate - a.rate);
    rankingByExercise[ex.id] = arr;
  });

  const isPlayerInSelectedTeam = (player: User) => {
    if (!selectedTeam) return true;

    const equipeStr = player.equipe || "";
    const equipeIds = equipeStr.split(",").map((id: string) => id.trim());
    return equipeIds.includes(String(selectedTeam));
  };

  const teamPlayers = players.filter(isPlayerInSelectedTeam);

  const pendingExercisesByPlayer = teamPlayers
    .map((player) => {
      const completedExerciseIds = new Set(
        evaluations
          .filter((evaluation) => evaluation.player_id === player.id)
          .map((evaluation) => evaluation.exercise_id)
      );

      const pendingExercises = exercises.filter(
        (exercise) => !completedExerciseIds.has(exercise.id)
      );

      return {
        player,
        pendingExercises,
      };
    })
    .filter(({ pendingExercises }) => pendingExercises.length > 0)
    .sort((a, b) => b.pendingExercises.length - a.pendingExercises.length);

  const handleTeamChange = (e: any) => {
    const value = Number(e.target.value);
    setSelectedTeam(value);
    localStorage.setItem("selectedTeam", String(value));
  };

  const handleClearSpecialAssignments = () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir réinitialiser aux dimensions par défaut ?"
      )
    ) {
      setSpecialAssignments(DEFAULT_SPECIAL_ASSIGNMENTS as any);
      saveSpecialAssignments(DEFAULT_SPECIAL_ASSIGNMENTS);
    }
  };

  const buildSessionPayload = () => {
    if (!selectedSession || !selectedTeam) return null;

    const playersOfTeam = players.filter((p) => {
      const equipeStr = p.equipe || "";
      const equipeIds = equipeStr.split(",").map((id: string) => id.trim());
      return equipeIds.includes(String(selectedTeam));
    });

    const session = sessions.find((s) => s.id === selectedSession);

    const payload = {
      team: teams.find((t) => t.id === selectedTeam) || null,
      session: session || null,
      generated_at: new Date().toISOString(),
      goal: {
        description:
          "Générer un programme d'entraînement personnalisé sur 2 semaines (4 sessions) pour chaque joueuse en fonction de leurs exercices les moins réussis.",
        horizon: {
          weeks: 2,
          sessions: 4,
        },
      },
      players: playersOfTeam.map((player) => {
        const evalsOfPlayer = evaluations.filter(
          (ev) =>
            ev.player_id === player.id &&
            (session ? ev.session_id === selectedSession : true)
        );

        // liste simple des exos de la session pour cette joueuse
        const exercisesDetails = evalsOfPlayer.map((ev) => {
          const ex = exerciseMap[ev.exercise_id];
          if (!ex) return null;

          let successRate = 0;
          try {
            const scores: number[] = JSON.parse(ev.details || "[]");
            const totalScore = scores.reduce((a, b) => a + b, 0);
            const maxPoints = ex.max_points || 0;
            const repetitions = ex.repetitions || 0;
            const maxPossible = maxPoints * repetitions;
            if (maxPossible > 0) {
              successRate = parseFloat(
                ((totalScore / maxPossible) * 100).toFixed(2)
              );
            }
          } catch {
            successRate = 0;
          }

          return {
            title: ex.title,
            description: ex.description,
            success_rate: successRate,
          };
        }).filter(Boolean) as {
          title: string;
          description: string;
          success_rate: number;
        }[];

        // on peut encore utiliser les agrégats existants pour repérer les faiblesses
        const catRates = computePlayerCategoryRates(player.id);
        const catAggregates = Object.entries(catRates).map(([cid, val]) => {
          const cat = categories.find((c) => c.id === Number(cid));
          return {
            category_id: Number(cid),
            category_name: cat?.name || null,
            rate: val.rate,
            count: val.count,
          };
        });

        const priorityCategories = [...catAggregates]
          .sort((a, b) => a.rate - b.rate)
          .slice(0, 2);

        const specialAggregates = specialCategories.map((special) => {
          const res = computeSpecialRates(
            player.id,
            special.id as
              | "mobilite"
              | "puissance"
              | "construction"
              | "agressivite"
          );
          return {
            dimension_id: special.id,
            dimension_label: special.name,
            rate: res.rate,
            count: res.count,
          };
        });

        // exos les plus faibles (basé uniquement sur success_rate)
        const weakExercises = exercisesDetails
          .filter((d) => d.success_rate < 60)
          .sort((a, b) => a.success_rate - b.success_rate);

        return {
          id: player.id,
          username: player.username,
          nom_complet: player.nom_complet,
          // uniquement ce qui est utile pour identifier la joueuse
          evaluations: exercisesDetails,
          aggregates: {
            by_category: catAggregates,
            by_special_dimension: specialAggregates,
          },
          summary_for_ai: {
            priority_categories: priorityCategories,
            weak_exercises: weakExercises,
          },
        };
      }),
    };

    return payload;
  };

  const sendSessionToWebhook = async () => {
    if (!selectedSession || !selectedTeam) return;

    const payload = buildSessionPayload();
    if (!payload) return;

    setIsExporting(true);
    try {
      console.log("Payload envoyé au webhook (n8n) :", payload);

      const res = await fetch(
        "https://n8n.atoneo.com/webhook/71b66dec-9f4a-4ea3-84e4-9bdcda7d413d",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const rawText = await res.text();
      console.log("Réponse n8n brute :", rawText);

      let markdown = rawText;

      // Le format attendu est: [{ "output": "..." }]
      try {
        const parsed = JSON.parse(rawText);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.output) {
          markdown = String(parsed[0].output);
        }
      } catch {
        // si ce n'est pas du JSON valide, on garde rawText
      }

      setPlanMarkdown(markdown || "Aucun contenu retourné.");
      setOpenPlanModal(true);
      console.log("Markdown extrait :", markdown);
    } catch (e) {
      console.error("Erreur lors de l'envoi au webhook :", e);
      setPlanMarkdown(
        "Erreur lors de la génération du plan d'entraînement.\n\n" +
          String(e)
      );
      setOpenPlanModal(true);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box
      sx={{
        padding: { xs: 2, sm: 4 },
        background: theme.palette.background.default,
        minHeight: "100vh",
        fontFamily: theme.typography.fontFamily,
        maxWidth: "1600px",
        margin: "0 auto",
        position: "relative",
      }}
    >
      {/* Overlay bloquant pendant le travail de l'IA */}
      {isExporting && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              px: 4,
              py: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              boxShadow: 4,
              maxWidth: 320,
              textAlign: "center",
            }}
          >
            <CircularProgress color="primary" />
            <Typography variant="h6">
              L’IA travaille sur les plans d’entraînement…
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Merci de patienter quelques instants, cette opération peut prendre
              plusieurs secondes.
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          marginBottom: 4,
          gap: 2,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: theme.typography.h1.fontSize,
            fontWeight: theme.typography.h1.fontWeight,
            color: theme.palette.primary.main,
            marginBottom: { xs: 2, sm: 0 },
          }}
        >
          Dashboard
        </Typography>
        <Box
          sx={{
            width: "100%",
            maxWidth: 700,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <FormControl sx={{ minWidth: 180, width: { xs: "100%", sm: "auto" } }}>
            <InputLabel id="team-select-label">Équipe</InputLabel>
            <Select
              labelId="team-select-label"
              value={selectedTeam || ""}
              label="Équipe"
              onChange={handleTeamChange}
            >
              <MenuItem value="">
                <em>Choisir une équipe</em>
              </MenuItem>
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220, width: { xs: "100%", sm: "auto" } }}>
            <InputLabel id="session-select-label">Session</InputLabel>
            <Select
              labelId="session-select-label"
              value={selectedSession || ""}
              label="Session"
              onChange={(e) => setSelectedSession(Number(e.target.value))}
              disabled={!selectedTeam || sessions.length === 0}
            >
              {(() => {
                const sortedSessions = [...sessions].sort(
                  (a, b) =>
                    new Date(b.start_date).getTime() -
                    new Date(a.start_date).getTime()
                );
                return sortedSessions.map((session) => (
                  <MenuItem key={session.id} value={session.id}>
                    {session.name} ({session.start_date})
                  </MenuItem>
                ));
              })()}
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            ml: { sm: 2 },
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/evaluation")}
            sx={{
              padding: { xs: "8px 16px", sm: "12px 24px" },
              fontSize: "1rem",
              fontWeight: "bold",
              backgroundColor: theme.palette.secondary.main,
              borderRadius: theme.shape.borderRadius,
              boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            }}
          >
            Faire une évaluation
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={sendSessionToWebhook}
            disabled={!selectedTeam || !selectedSession || isExporting}
            sx={{
              padding: { xs: "8px 16px", sm: "12px 24px" },
              fontSize: "1rem",
              fontWeight: "bold",
              borderRadius: theme.shape.borderRadius,
              boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            }}
          >
            {isExporting
              ? "Préparation / envoi..."
              : "Générer entraînements par IA"}
          </Button>
        </Box>
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={useCoef}
            onChange={() => setUseCoef((v) => !v)}
            color="primary"
          />
        }
        label="Activer les coefficients de niveau d'exercice"
        sx={{ mb: 2 }}
      />

      <Button
        variant="outlined"
        color="error"
        onClick={handleClearSpecialAssignments}
        sx={{
          ml: 2,
          mb: 2,
        }}
      >
        Réinitialiser les dimensions supp.
      </Button>

      <Typography
        variant="h2"
        sx={{
          fontSize: theme.typography.h2.fontSize,
          fontWeight: theme.typography.h2.fontWeight,
          color: theme.palette.secondary.main,
          mb: 2,
          mt: 4,
        }}
      >
        Profils des joueuses
      </Typography>

      <PlayerRadarComparison
        players={teamPlayers}
        categories={categories}
        specialCategories={specialCategories}
        computePlayerCategoryRates={computePlayerCategoryRates}
        computeSpecialRates={computeSpecialRates}
        specialAssignments={specialAssignments}
      />

      <Typography
        variant="h2"
        sx={{
          fontSize: theme.typography.h2.fontSize,
          fontWeight: theme.typography.h2.fontWeight,
          color: theme.palette.secondary.main,
          mb: 2,
        }}
      >
        Classement par catégorie
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
          gap: 3,
        }}
      >
        {categories.map((cat) => {
          const ranking = rankingByCategory[cat.id];
          const totalCatExos = selectedSession
            ? exercises.filter((ex) => ex.category_id === cat.id).length
            : undefined;

          return (
            <CategoryRanking
              key={cat.id}
              category={cat}
              ranking={ranking}
              totalExercises={totalCatExos}
            />
          );
        })}

        {specialCategories.map((special) => {
          const ranking = rankingBySpecial[special.id];
          const totalExercises = countExercisesForSpecialCategory(
            specialAssignments as any,
            special.id
          );

          return (
            <CategoryRanking
              key={special.id}
              category={special}
              ranking={ranking}
              isSpecial={true}
              totalExercises={totalExercises}
            />
          );
        })}
      </Box>

      <Typography
        variant="h2"
        sx={{
          fontSize: theme.typography.h2.fontSize,
          fontWeight: theme.typography.h2.fontWeight,
          color: theme.palette.secondary.main,
          mt: 6,
          mb: 2,
        }}
      >
        Classement par exercice
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
          gap: 3,
        }}
      >
        {exercises.map((ex) => {
          const cat = categories.find((c) => c.id === ex.category_id);
          const ranking = rankingByExercise[ex.id];
          const assignments = specialAssignments[ex.id] || {
            mobilite: false,
            puissance: false,
            construction: false,
            agressivite: false,
          };

          return (
            <ExerciseRanking
              key={ex.id}
              exercise={ex}
              categoryName={cat?.name}
              ranking={ranking}
              assignments={assignments}
              onToggleCategory={toggleSpecialCategory}
            />
          );
        })}
      </Box>

      <Typography
        variant="h2"
        sx={{
          fontSize: theme.typography.h2.fontSize,
          fontWeight: theme.typography.h2.fontWeight,
          color: theme.palette.secondary.main,
          mt: 6,
          mb: 2,
        }}
      >
        Exos en attente{selectedSession
          ? ` : ${sessions.find((session) => session.id === selectedSession)?.name || "session sélectionnée"}`
          : ""}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
          gap: 3,
          mb: 4,
        }}
      >
        {pendingExercisesByPlayer.length > 0 ? (
          pendingExercisesByPlayer.map(({ player, pendingExercises }) => (
            <Box
              key={player.id}
              sx={{
                borderRadius: theme.shape.borderRadius,
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                p: 2,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 1,
                }}
              >
                {player.nom_complet || player.username}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 1, fontWeight: 600 }}
              >
                Exos en attente : {pendingExercises.length}
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                {pendingExercises.map((exercise) => {
                  const category = categories.find((cat) => cat.id === exercise.category_id);
                  return (
                    <Box
                      component="li"
                      key={exercise.id}
                      sx={{
                        color: theme.palette.text.primary,
                        mb: 0.5,
                        fontSize: "0.92rem",
                      }}
                    >
                      {exercise.title}
                      {category ? (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 0.75, color: theme.palette.text.secondary }}
                        >
                          ({category.name})
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Aucune joueuse n'a d'exercice en attente sur la session sélectionnée.
          </Typography>
        )}
      </Box>

      {/* Modale d'affichage du plan IA en Markdown */}
      <Dialog
        open={openPlanModal}
        onClose={() => setOpenPlanModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Plan d'entraînement généré par l'IA</DialogTitle>
        <DialogContent
          dividers
          sx={{
            backgroundColor: theme.palette.background.default,
          }}
        >
          {planMarkdown ? (
            <Box
              sx={{
                "& h1": {
                  ...theme.typography.h4,
                  mb: 2,
                },
                "& h2": {
                  ...theme.typography.h5,
                  mt: 2,
                  mb: 1,
                },
                "& h3": {
                  ...theme.typography.subtitle1,
                  mt: 2,
                  mb: 1,
                  fontWeight: 600,
                },
                "& p": {
                  ...theme.typography.body2,
                  mb: 1,
                },
                "& ul, & ol": {
                  pl: 3,
                  mb: 1.5,
                },
                "& table": {
                  width: "100%",
                  borderCollapse: "collapse",
                  mb: 2,
                },
                "& th, & td": {
                  border: `1px solid ${theme.palette.divider}`,
                  padding: "4px 8px",
                  fontSize: "0.85rem",
                },
                "& thead th": {
                  backgroundColor: theme.palette.action.hover,
                  fontWeight: 600,
                },
                "& code": {
                  fontFamily: '"Fira Code", "Consolas", "Menlo", monospace',
                  fontSize: "0.85rem",
                  backgroundColor: theme.palette.action.hover,
                  px: 0.5,
                  borderRadius: 0.5,
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {planMarkdown}
              </ReactMarkdown>
            </Box>
          ) : (
            <Typography variant="body2">
              Aucun contenu à afficher pour le moment.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlanModal(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
export default Dashboard;
